import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { styled } from "@mui/material/styles"
import Rating from "@mui/material/Rating"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Grid from "@mui/material/Grid"
import CloseIcon from "@mui/icons-material/Close"
import { isLoaded, setSnackbar } from "../../../actions/globalUiActions"
import { getUserLessons, updateUserOption } from "../../../actions/userActions"

import { formatDate } from "../../../utils/dateUtils"
import { logError } from "../../../utils/errors"
import { getReview, saveReview } from "../../../utils/reviewUtils"
// Show if signed in, have a lesson
// If show, then prompt for review if haven't left one, else prompt for Google.

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": {
    color: theme.palette.accentTwo.main,
  },
  "& .MuiRating-iconEmpty": {
    color: theme.palette.accentTwo.main,
  },
}))

class ReviewAlert extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      lesson: null,
      leftReview: false,
      rating: 0,
      review: "",
    }

    if (props.auth && props.auth.uid) {
      this.props.getUserLessons(props.auth.uid)
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.auth !== nextProps.auth && nextProps.auth.uid) {
      this.props.getUserLessons(nextProps.auth.uid)
    }
    if (
      this.props.lessons !== nextProps.lessons &&
      nextProps.lessons &&
      nextProps.lessons.length > 0 &&
      nextProps.auth?.uid
    ) {
      this.processLessons(nextProps.lessons, nextProps.auth.uid)
    }
  }

  handleChange = (e) => this.setState({ [e.target.name]: e.target.value })

  handleSubmit = (e) => {
    e.preventDefault()
    const { rating, review, lesson } = this.state

    const reviewObj = {
      text: review,
      rating,
      date: new Date().valueOf(),
    }
    const student = {
      name: this.props.profile.userName,
      id: this.props.auth.uid,
    }

    saveReview(lesson.instructor.id, lesson.id, student, reviewObj, "alert-bar")
      .then(() => {
        this.setState({ leftReview: true })
      })
      .catch(() => {
        this.props.setSnackbar(
          "We encountered a problem while saving your review",
          "error",
        )
      })
  }

  processLessons = (lessons, userId) => {
    const sixtyDays = 1000 * 60 * 60 * 24 * 60
    // Just get lessons that have already occurred less than 60 days ago.
    const prevLessons = lessons.filter((lesson) =>
      lesson.details
        ? lesson.details.startTime < new Date().valueOf() &&
          new Date().valueOf() - lesson.details.startTime < sixtyDays
        : false,
    )

    if (prevLessons.length > 0) {
      // Now check each of their lessons for a review.
      const proms = []
      let leftReview = true
      prevLessons.forEach((lesson) => {
        proms.push(getReview(lesson.instructor.id, lesson.id, userId))
      })

      Promise.all(proms)
        .then((results) => {
          results.forEach((result, i) => {
            // leftReview === true just means it hasn't yet found one
            // without a review.
            if (!result && leftReview === true) {
              this.setState({ lesson: prevLessons[i] })
              leftReview = false
            }
          })
          if (leftReview) {
            this.setState({ leftReview: true })
          }
        })
        .catch(logError)
    }
  }

  renderReviewForm = () => {
    const { rating, review } = this.state

    return (
      <form onSubmit={this.handleSubmit}>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item>
            <div style={{ fontSize: 30 }}>
              <StyledRating
                name="reviewRating"
                rating={rating}
                onChange={(event, newValue) =>
                  this.setState({ rating: newValue })
                }
                size="large"
              />
            </div>
          </Grid>
          {rating ? (
            <Grid item>
              <TextField
                variant="outlined"
                label="Add a brief review?"
                value={review}
                onChange={this.handleChange}
                name="review"
                margin="dense"
                style={{ width: "100%" }}
                fullWidth
                multiline
              />
            </Grid>
          ) : null}
          {rating ? (
            <Grid item>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                style={{ marginTop: 20 }}
              >
                Submit Rating
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </form>
    )
  }

  renderOnSiteReviewCTA = () => {
    const {
      lesson: { instructor, details },
    } = this.state

    const instructorName = instructor.displayName || instructor.firstName

    return (
      <div style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>
          How would you rate your lesson with {instructorName} on{" "}
          {formatDate(details.startTime, instructor.timeZone, "long")}?
        </h3>
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "left" }}>
          {this.renderReviewForm()}
        </div>
      </div>
    )
  }

  renderGoogleReviewCTA = () => (
    <h4 style={{ margin: 0 }}>
      Thank you for your rating. Want to spread the word about Coastline? Please
      consider leaving a review on{" "}
      <a
        href="https://goo.gl/maps/VThYX8LT78w"
        target="_blank"
        rel="noopener noreferrer"
      >
        Google Reviews
      </a>
      . Have a great day!
    </h4>
  )

  isLessonFinished = () =>
    this.state.lesson?.details.endTime < new Date().valueOf()

  renderSection = () => {
    if (this.state.leftReview) {
      return this.renderGoogleReviewCTA()
    }
    return this.renderOnSiteReviewCTA()
  }

  render() {
    // Only render if they're signed in and a lesson is set to review,
    // or they've already left a review (but haven't left a Google review).
    if (
      !isLoaded(this.props.auth) ||
      !this.props.auth.uid ||
      this.props.profile.isAdmin ||
      this.props.profile.isInstructor ||
      (!this.state.lesson && !this.state.leftReview) ||
      (this.props.userOptions && this.props.userOptions.hideReview)
    ) {
      return <span data-cy="review-cta-alert-hidden" />
    }

    if (this.isLessonFinished()) {
      return (
        <div
          style={{ padding: 20, textAlign: "center" }}
          data-cy="review-cta-alert"
          className="section-highlight"
        >
          <div
            style={{
              float: "right",
              marginTop: this.state.leftReview ? -14 : 0,
            }}
          >
            <IconButton
              onClick={() => this.props.updateUserOption({ hideReview: true })}
              data-cy="review-cta-close"
              size="large"
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {this.renderSection()}
        </div>
      )
    }
    return <span data-cy="review-cta-alert-hidden" />
  }
}

ReviewAlert.propTypes = {
  auth: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  getUserLessons: PropTypes.func.isRequired,
  lessons: PropTypes.array,
  userOptions: PropTypes.object,
  updateUserOption: PropTypes.func.isRequired,
}

ReviewAlert.defaultProps = {
  lessons: null,
  userOptions: null,
}

const mapStateToProps = (state) => {
  const {
    firebase: { auth, profile },
    user: { lessons, userOptions },
  } = state

  return {
    auth,
    profile,
    lessons,
    userOptions,
  }
}

export default connect(mapStateToProps, {
  getUserLessons,
  updateUserOption,
  setSnackbar,
})(ReviewAlert)
